import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "selectedList",
    "selectedEmptyMessage",
    "selectionCount",
    "publisherSearch",
    "publisherRow",
    "usersCount",
    "margin",
    "seatFee",
    "annualDiscount",
    "warning",
    "priceUserMonth",
    "monthlyTotal",
    "annualTotal",
    "annualTotalDiscounted",
    "annualRetail",
    "savingsAbs",
    "savingsPct"
  ]

  static values = {
    calculateUrl: String,
    csrfToken: String
  }

  connect() {
    this.selectedPublishers = []
    this.lastCalculationData = null
    this.renderSelectedPublishers()
    if (this.hasPublisherSearchTarget) {
      this.filterPublishers({ target: this.publisherSearchTarget })
    }
  }

  addPublisher(event) {
    event.preventDefault()
    const button = event.currentTarget
    const id = parseInt(button.dataset.publisherId, 10)
    if (this.selectedPublishers.some((pub) => pub.id === id)) return

    this.selectedPublishers.push({
      id,
      name: button.dataset.publisherName,
      msrp: parseFloat(button.dataset.publisherMsrp || "0"),
      npPrice: parseFloat(button.dataset.publisherNpPrice || "0")
    })

    this.renderSelectedPublishers()
  }

  removePublisher(event) {
    event.preventDefault()
    const id = parseInt(event.currentTarget.dataset.publisherId, 10)
    this.selectedPublishers = this.selectedPublishers.filter((pub) => pub.id !== id)
    this.renderSelectedPublishers()
  }

  renderSelectedPublishers() {
    this.selectedListTarget.innerHTML = ""
    this.selectionCountTarget.textContent = this.selectedPublishers.length

    if (this.selectedPublishers.length === 0) {
      this.selectedEmptyMessageTarget.classList.remove("d-none")
      return
    }

    this.selectedEmptyMessageTarget.classList.add("d-none")

    this.selectedPublishers.forEach((publisher) => {
      const row = document.createElement("div")
      row.className = "list-group-item d-flex justify-content-between align-items-center"
      row.innerHTML = `
        <div>
          <div class="fw-bold">${publisher.name}</div>
          <small class="text-muted">MSRP ${this.formatCurrency(publisher.msrp)} • NP ${this.formatCurrency(publisher.npPrice)}</small>
        </div>
        <button class="btn btn-sm btn-outline-danger" data-action="pricing-calculator#removePublisher" data-publisher-id="${publisher.id}">
          Remove
        </button>
      `
      this.selectedListTarget.appendChild(row)
    })
  }

  async recalculate(event) {
    event.preventDefault()

    if (this.selectedPublishers.length === 0) {
      this.showAlert("Please select at least one publisher before recalculating.")
      return
    }

    const payload = {
      publisher_ids: this.selectedPublishers.map((pub) => pub.id),
      users_count: parseInt(this.usersCountTarget.value || "0", 10),
      margin: parseFloat(this.marginTarget.value || "0"),
      seat_fee: parseFloat(this.seatFeeTarget.value || "0"),
      annual_discount: parseFloat(this.annualDiscountTarget.value || "0")
    }

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorBody = await response.text()
        this.showAlert("Unable to calculate pricing.")
        console.error("Pricing calculator error:", response.status, errorBody)
        return
      }

      const data = await response.json()

      this.lastCalculationData = data
      this.hideAlert()
      this.updateResults(data.results)
    } catch (error) {
      console.error("Pricing calculator fetch error:", error)
      this.showAlert("Unexpected error while calculating pricing.")
    }
  }

  updateResults(data) {
    this.priceUserMonthTarget.textContent = this.formatCurrency(data.price_user_month)
    this.monthlyTotalTarget.textContent = this.formatCurrency(data.monthly_total)
    this.annualTotalTarget.textContent = this.formatCurrency(data.annual_total)
    this.annualTotalDiscountedTarget.textContent = this.formatCurrency(data.annual_total_discounted)
    this.annualRetailTarget.textContent = this.formatCurrency(data.annual_retail)
    this.savingsAbsTarget.textContent = this.formatCurrency(data.savings_abs)
    this.savingsPctTarget.textContent = this.formatPercentage(data.savings_pct)

    if (data.warning) {
      this.showAlert("Savings are zero or negative. Adjust inputs or selected publishers.", "danger")
    } else {
      this.hideAlert()
    }
  }

  formatCurrency(value) {
    const number = parseFloat(value || "0")
    return `$${number.toFixed(2)}`
  }

  formatPercentage(value) {
    const number = parseFloat(value || "0")
    return `${(number * 100).toFixed(2)}%`
  }

  showAlert(message, type = "danger") {
    this.warningTarget.textContent = message
    this.warningTarget.classList.remove("d-none", "alert-danger", "alert-warning")
    this.warningTarget.classList.add(`alert-${type}`)
  }

  hideAlert() {
    this.warningTarget.classList.add("d-none")
    this.warningTarget.textContent = ""
  }

  filterPublishers(event) {
    if (!this.hasPublisherRowTarget) return

    const query = (event.target?.value || "").toLowerCase().trim()
    this.publisherRowTargets.forEach((row) => {
      const name = row.dataset.publisherName || ""
      row.classList.toggle("d-none", !name.includes(query))
    })
  }

  exportToCSV(event) {
    event.preventDefault()

    if (!this.lastCalculationData) {
      this.showAlert("Please calculate pricing first before exporting.", "warning")
      return
    }

    const csv = this.generateCSV(this.lastCalculationData)
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)

    link.setAttribute("href", url)
    link.setAttribute("download", `pricing_calculation_${this.getTimestamp()}.csv`)
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  generateCSV(data) {
    const lines = []

    // Header
    lines.push("Nicklpass Pricing Calculator Results")
    lines.push("")

    // Selected Publishers
    lines.push("Selected Publishers")
    lines.push("Name,MSRP,NP Price")
    data.publishers.forEach((pub) => {
      const msrp = parseFloat(pub.msrp || 0).toFixed(2)
      const npPrice = parseFloat(pub.np_price || 0).toFixed(2)
      lines.push(`"${pub.name}",${msrp},${npPrice}`)
    })
    lines.push("")

    // Input Parameters
    lines.push("Input Parameters")
    lines.push("Parameter,Value")
    lines.push(`Users Count,${data.inputs.users_count || 0}`)
    lines.push(`Margin (%),${(parseFloat(data.inputs.margin || 0) * 100).toFixed(2)}`)
    lines.push(`Seat Fee ($),${parseFloat(data.inputs.seat_fee || 0).toFixed(2)}`)
    lines.push(`Annual Discount (%),${(parseFloat(data.inputs.annual_discount || 0) * 100).toFixed(2)}`)
    lines.push("")

    // Results
    lines.push("Calculation Results")
    lines.push("Metric,Value")
    lines.push(`Nicklpass Monthly Total/User,${this.formatCurrency(data.results.price_user_month)}`)
    lines.push(`Monthly Total,${this.formatCurrency(data.results.monthly_total)}`)
    lines.push(`Annual Total,${this.formatCurrency(data.results.annual_total)}`)
    lines.push(`Annual Total (Discounted),${this.formatCurrency(data.results.annual_total_discounted)}`)
    lines.push(`Retail Annual Total,${this.formatCurrency(data.results.annual_retail)}`)
    lines.push(`Savings (Abs),${this.formatCurrency(data.results.savings_abs)}`)
    lines.push(`Savings (%),${this.formatPercentage(data.results.savings_pct)}`)

    return lines.join("\n")
  }

  getTimestamp() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const hours = String(now.getHours()).padStart(2, "0")
    const minutes = String(now.getMinutes()).padStart(2, "0")
    const seconds = String(now.getSeconds()).padStart(2, "0")
    return `${year}${month}${day}_${hours}${minutes}${seconds}`
  }
}
